import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  InputAdornment,
  Box,
  MenuItem,
  TextField,
  Typography,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  Stack,
  Fade,
  FormControlLabel,
  Checkbox,
  IconButton,
  RadioGroup,
  Radio,
} from "@mui/material";
import {
  Add as AddIcon,
  ExpandMore as ExpandMoreIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";

const alignmentOptions = ["left", "center", "right"];
const geometryFieldConfigs = [
  { key: "x", label: "Left", aliases: ["x", "X", "left"], decimals: 5 },
  { key: "width", label: "Width", aliases: ["width", "W"], decimals: 5 },
  { key: "y", label: "Top", aliases: ["y", "Y", "top"], decimals: 5 },
  { key: "height", label: "Height", aliases: ["height", "H"], decimals: 5 },
];

const getValue = (field, ...keys) => {
  for (const key of keys) {
    if (field?.[key] !== undefined && field?.[key] !== null) {
      return field[key];
    }
  }
  return "";
};

const formatNumber = (value, decimals = 5, integerOnly = false) => {
  if (
    value === "" ||
    value === null ||
    value === undefined ||
    Number.isNaN(Number(value))
  ) {
    return "";
  }

  const numericValue = Number(value);

  if (integerOnly) {
    return String(Math.round(numericValue));
  }

  return numericValue
    .toFixed(decimals)
    .replace(/\.0+$/, "")
    .replace(/(\.\d*?)0+$/, "$1");
};

const sectionSx = {
  boxShadow: "none",
  border: "1px solid #e8eaed",
  borderRadius: "10px !important",
  "&:before": { display: "none" },
};

const OPERATORS = [
  { value: "+", label: "+ Add" },
  { value: "-", label: "− Subtract" },
  { value: "*", label: "× Multiply" },
  { value: "/", label: "÷ Divide" },
  { value: "%", label: "% Percentage of" },
];

const OP_SYMBOL = { "+": "+", "-": "−", "*": "×", "/": "÷", "%": "%" };

const ParentFieldDialog = ({
  open,
  allFields,
  selectedKeys,
  onClose,
  onConfirm,
}) => {
  const [pending, setPending] = React.useState([]);

  React.useEffect(() => {
    if (open) {
      setPending(selectedKeys);
    }
  }, [open, selectedKeys]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1, fontSize: 14, fontWeight: 600 }}>
        Setup Computed Value
      </DialogTitle>

      <DialogContent sx={{ pt: "8px !important" }}>
        <Autocomplete
          multiple
          value={pending}
          onChange={(event, newValue) => {
            setPending(newValue);
          }}
          getOptionLabel={(option) => option.field_label}
          isOptionEqualToValue={(option, value) => option.name === value.name}
          filterSelectedOptions={true}
          options={allFields
            ?.filter((f) => !["PDFRectangle", "PDFLine"].includes(f.type))
            ?.map((f) => ({
              field_instance_id: f.field_instance_id,
              name: f.name,
              value: "",
              field_label: f.field_label,
              options: f.options,
            }))}
          fullWidth
          renderInput={(params) => (
            <TextField
              {...params}
              label="Select Parent Fields"
              name="field_type"
            />
          )}
        />

        <Stack gap={1} mt={1}>
          {pending?.map((field) => (
            <Box
              display={"flex"}
              alignItems={"center"}
              justifyContent={"space-between"}
              key={field.field_instance_id}
              gap={1}
            >
              <Typography sx={{ minWidth: "30%" }}>
                {field?.field_label}
              </Typography>
              <Autocomplete
                multiple
                size="small"
                value={field?.value || []}
                sx={{ minWidth: "68%" }}
                filterSelectedOptions
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, value) =>
                  option.value === value.value
                }
                onChange={(event, newValue) => {
                  const updatedValue = pending.map((p) =>
                    p.field_instance_id === field.field_instance_id
                      ? { ...p, value: newValue }
                      : p,
                  );
                  setPending(updatedValue);
                }}
                limitTags={1}
                renderValue={(values, getItemProps) =>
                  values.map((option, index) => {
                    const { key, ...itemProps } = getItemProps({ index });
                    return (
                      <Chip
                        key={key}
                        variant="outlined"
                        label={option.label}
                        size="small"
                        {...itemProps}
                      />
                    );
                  })
                }
                options={field?.options}
                fullWidth
                renderInput={(params) => (
                  <TextField {...params} size="small" label="Select Values" />
                )}
              />
            </Box>
          ))}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 2, pb: 1.5 }}>
        <Button size="small" onClick={onClose}>
          Cancel
        </Button>
        <Button
          size="small"
          variant="contained"
          onClick={() => onConfirm(pending)}
        >
          Add
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const EMPTY_CALC_FIELDS = [{ field: "" }, { field: "", operator: "" }];
const EMPTY_SAME_FIELDS = [{ field: "" }];
const EQ_OPERATOR = { value: "=", label: "= Same as" };

const FormulaDialog = ({
  open,
  onClose,
  fieldOptions,
  formulaFields,
  onFieldChange,
}) => {
  const [mode, setMode] = useState("calc");
  const fields = formulaFields?.length > 0 ? formulaFields : null;

  const isSameAs = useMemo(() => {
    if (fields?.length > 0) {
      return fields.some((f) => f?.operator?.value === "=");
    }
    return mode === "same";
  }, [fields, mode]);

  useEffect(() => {
    if (fields?.length > 0) {
      setMode(isSameAs ? "same" : "calc");
    }
  }, [fields, isSameAs]);

  const hasArithmeticOp = useMemo(
    () =>
      fields?.some((f) => ["+", "-", "*", "/"].includes(f?.operator?.value)) ??
      false,
    [fields],
  );

  const selectedFieldNames = useMemo(
    () => new Set(fields?.map((f) => f?.field?.name).filter(Boolean)),
    [fields],
  );

  const filteredOperators = useMemo(
    () => OPERATORS.filter((opt) => !(hasArithmeticOp && opt?.value === "=")),
    [hasArithmeticOp],
  );

  const filteredFieldOptions = useMemo(
    () => fieldOptions?.filter((opt) => !selectedFieldNames.has(opt?.name)),
    [fieldOptions, selectedFieldNames],
  );

  const handleChangeFormulaField = useCallback(
    (name, val, index) => {
      const updated =
        fields != null
          ? fields.map((f, i) => (i === index ? { ...f, [name]: val } : f))
          : [
              {
                [name]: val,
                ...(isSameAs ? { operator: EQ_OPERATOR } : {}),
              },
              ...(isSameAs ? [] : [{ field: "", operator: "" }]),
            ];

      onFieldChange?.("formulaFields", updated);
    },
    [fields, isSameAs, onFieldChange],
  );

  const handleModeChange = useCallback(
    (e) => {
      setMode(e.target.value);
      onFieldChange?.("formulaFields", "");
    },
    [onFieldChange],
  );

  const handleAddRow = useCallback(() => {
    onFieldChange?.("formulaFields", [
      ...(fields ?? []),
      { field: "", operator: "" },
    ]);
  }, [fields, onFieldChange]);

  const displayFields =
    fields ?? (isSameAs ? EMPTY_SAME_FIELDS : EMPTY_CALC_FIELDS);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ pb: 1, fontSize: 14, fontWeight: 600 }}>
        Formula builder
      </DialogTitle>

      <DialogContent sx={{ pt: "8px !important" }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography
            variant="caption"
            sx={{
              display: "block",
              mb: 1.5,
              fontSize: "14px !important",
              color: "#666",
            }}
          >
            Add field
          </Typography>

          <RadioGroup row value={mode} onChange={handleModeChange}>
            <FormControlLabel
              value="same"
              control={<Radio />}
              label="Use Same Value"
            />
            <FormControlLabel
              value="calc"
              control={<Radio />}
              label="Calculate Field"
            />
          </RadioGroup>
        </Box>

        <Stack spacing={0.75}>
          {displayFields.map((f, i) => (
            <Stack
              key={i}
              direction="row"
              spacing={1}
              alignItems="center"
              mb={2}
            >
              {i !== 0 && (
                <Autocomplete
                  size="small"
                  value={f?.operator ?? ""}
                  onChange={(_, newValue) =>
                    handleChangeFormulaField("operator", newValue, i)
                  }
                  getOptionLabel={(opt) => opt?.label ?? ""}
                  options={filteredOperators}
                  fullWidth
                  renderInput={(params) => (
                    <TextField {...params} label="Operator" name="operator" />
                  )}
                />
              )}

              <Autocomplete
                size="small"
                value={f?.field ?? ""}
                onChange={(_, newValue) =>
                  handleChangeFormulaField("field", newValue, i)
                }
                getOptionLabel={(opt) =>
                  opt?.field_label ? `${opt.field_label} (${opt.name})` : ""
                }
                options={filteredFieldOptions}
                fullWidth
                renderInput={(params) => (
                  <TextField {...params} label="Select field" name="field" />
                )}
              />

              {f?.operator?.value === "%" && (
                <TextField
                  label="Value"
                  size="small"
                  type="number"
                  style={{ minWidth: "150px" }}
                  value={f?.value ?? ""}
                  onChange={(event) =>
                    handleChangeFormulaField("value", event.target.value, i)
                  }
                />
              )}

              {!isSameAs && (
                <IconButton
                  onClick={handleAddRow}
                  disabled={!f?.field}
                  sx={{
                    border: "1px solid #d0d7de",
                    borderRadius: 1.5,
                    p: "6px !important",
                  }}
                >
                  <AddIcon />
                </IconButton>
              )}
            </Stack>
          ))}
        </Stack>

        {!isSameAs && (
          <>
            <Typography
              variant="caption"
              sx={{
                display: "block",
                mb: 1.5,
                fontSize: "14px !important",
                color: "#666",
                mt: 2.5,
              }}
            >
              Formula preview
            </Typography>
            <Box
              sx={{
                bgcolor: "action.hover",
                borderRadius: 1,
                p: 1.5,
                minHeight: 48,
                display: "flex",
                flexWrap: "wrap",
                gap: 0.75,
                alignItems: "center",
              }}
            >
              {!fields ? (
                <Typography
                  variant="body2"
                  color="text.disabled"
                  fontStyle="italic"
                >
                  No fields added yet
                </Typography>
              ) : (
                fields.map((e, i) => (
                  <Box
                    key={i}
                    sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                  >
                    {i > 0 && e?.operator?.value && (
                      <Typography>{OP_SYMBOL[e.operator.value]}</Typography>
                    )}
                    {e?.field?.name ? (
                      <Typography>{e.field.name}</Typography>
                    ) : (
                      <Typography>{e.value}</Typography>
                    )}
                  </Box>
                ))
              )}
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2.5, py: 1.5 }}>
        <Button onClick={onClose} variant="contained" size="small">
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const MIN_WIDTH = 280;
const MAX_WIDTH = 600;
const DEFAULT_WIDTH = 330;

const ObjectInspectorDrawer = ({
  isOpen,
  selectedField,
  selectedTextItem,
  pdfTextEdits,
  currentDocument,
  onFieldChange,
  loading,
  allFields,
  textOverlayDisplayMode,
  handleRemoveFieldFromRow,
}) => {
  const [expandedSections, setExpandedSections] = useState({
    geometry: true,
    general: true,
    appearance: true,
    advance: false,
  });
  const [geometryDrafts, setGeometryDrafts] = useState({});
  const [parentsDialogOpen, setParentsDialogOpen] = useState(false);
  const [configureFormulaOpen, setConfigureFormulaOpen] = useState(false);

  const [width, setWidth] = useState(DEFAULT_WIDTH);
  const isResizing = useRef(false);
  const startX = useRef(0);
  const startWidth = useRef(0);

  const parents = React.useMemo(() => {
    const raw = selectedField?.parents;
    if (!raw) return [];
    return Array.isArray(raw) ? raw : [raw];
  }, [selectedField?.parents]);

  const selectFields = useMemo(
    () =>
      allFields?.filter((f) =>
        ["selectGroup", "checkboxGroup"].includes(f?.field_type),
      ),
    [allFields],
  );

  const selectedCombineField = allFields?.find(
    (f) => f?.name === selectedField?.name,
  );

  React.useEffect(() => {
    if (!selectedField) {
      setGeometryDrafts({});
      return;
    }

    const nextGeometryDrafts = geometryFieldConfigs.reduce(
      (acc, fieldConfig) => {
        const rawValue = getValue(selectedField, ...fieldConfig.aliases);
        const displayValue = rawValue === "" ? "" : Number(rawValue);

        acc[fieldConfig.key] = formatNumber(displayValue, fieldConfig.decimals);
        return acc;
      },
      {},
    );

    setGeometryDrafts(nextGeometryDrafts);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedField]);

  const handleSectionToggle = (sectionKey) => (_, expanded) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionKey]: expanded,
    }));
  };

  const handleChange = (fieldKey, parser) => (event) => {
    const rawValue = event.target.value;
    const nextValue = parser ? parser(rawValue) : rawValue;
    onFieldChange?.(fieldKey, nextValue);
  };

  const commitGeometryValue = (fieldKey, aliases, decimals = 5) => {
    const draftValue = geometryDrafts[fieldKey];

    if (draftValue === "") {
      onFieldChange?.(fieldKey, "");
      return;
    }

    const parsedValue = Number(draftValue);
    if (!Number.isFinite(parsedValue)) {
      const originalValue = getValue(selectedField, ...aliases);
      const displayValue = originalValue === "" ? "" : Number(originalValue);
      setGeometryDrafts((prev) => ({
        ...prev,
        [fieldKey]: formatNumber(displayValue, decimals),
      }));
      return;
    }

    onFieldChange?.(fieldKey, parsedValue);
    setGeometryDrafts((prev) => ({
      ...prev,
      [fieldKey]: formatNumber(parsedValue, decimals),
    }));
  };
  const onMouseDown = useCallback(
    (e) => {
      isResizing.current = true;
      startX.current = e.clientX;
      startWidth.current = width;

      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";

      const onMouseMove = (e) => {
        if (!isResizing.current) return;
        const delta = startX.current - e.clientX;
        const newWidth = Math.min(
          MAX_WIDTH,
          Math.max(MIN_WIDTH, startWidth.current + delta),
        );
        setWidth(newWidth);
      };

      const onMouseUp = () => {
        isResizing.current = false;
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
        document.removeEventListener("mousemove", onMouseMove);
        document.removeEventListener("mouseup", onMouseUp);
      };

      document.addEventListener("mousemove", onMouseMove);
      document.addEventListener("mouseup", onMouseUp);
    },
    [width],
  );

  const selectedTextEdit = React.useMemo(() => {
    if (!selectedTextItem) return null;
    return (pdfTextEdits || []).find(
      (edit) => edit.target_item_id === selectedTextItem.id,
    );
  }, [pdfTextEdits, selectedTextItem]);

  const groupedFields = useMemo(() => {
    if (!selectedField?.row) return [];
    const rowPrefix = selectedField?.row?.split("_")?.[0];

    return allFields
      ?.filter(
        (f) =>
          f?.row?.includes(rowPrefix) ||
          f?.options?.some((opt) => opt?.row?.includes(rowPrefix)),
      )
      ?.map((f) => ({
        label: f?.field_label || f?.label,
        name: f?.name,
        row: f?.row,
        options: f?.options?.map((opt) => opt?.name),
      }));
  }, [allFields, selectedField]);

  return (
    <Fade in>
      <Box sx={{ display: "flex", position: "relative" }}>
        {isOpen && (
          <Box
            onMouseDown={onMouseDown}
            sx={{
              width: 5,
              cursor: "col-resize",
              bgcolor: "transparent",
              borderRight: "1px solid #E5E7EB",
              flexShrink: 0,
              transition: "background-color 0.15s",
              "&:hover": {
                bgcolor: "primary.main",
                opacity: 0.4,
              },
              "&:active": {
                bgcolor: "primary.main",
                opacity: 0.6,
              },
            }}
          />
        )}
        <Box
          sx={{
            width: isOpen ? width : 0,
            minWidth: isOpen ? MIN_WIDTH : 0,
            maxWidth: isOpen ? MAX_WIDTH : 0,
            height: "100%",
            transition: "all 260ms ease",
            overflow: "hidden",
            borderLeft: isOpen ? "1px solid #e0e0e0" : "none",
            backgroundColor: "#fff",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box sx={{ p: 2, borderBottom: "1px solid #f0f0f0" }}>
            <Typography
              variant="subtitle1"
              align="center"
              sx={{ fontWeight: 700 }}
            >
              {selectedTextItem
                ? "PDF Text"
                : selectedField && selectedField?.type === "PDFTextField"
                  ? "Text Field"
                  : selectedField && selectedField?.type === "PDFCheckBox"
                    ? "Check Box"
                    : "Object Inspector"}
            </Typography>
          </Box>

          <Box
            sx={{
              p: 2,
              display: "flex",
              flexDirection: "column",
              gap: 1.5,
              overflowY: "auto",
              height: "calc(100% - 158px)",
            }}
          >
            {selectedTextItem ? (
              <>
                <Accordion
                  expanded={expandedSections.geometry}
                  onChange={handleSectionToggle("geometry")}
                  disableGutters
                  sx={sectionSx}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography sx={{ fontWeight: 600 }}>
                      PDF Text Geometry
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: 1.25,
                      }}
                    >
                      {[
                        [
                          "x",
                          selectedTextEdit?.rect?.X ??
                            selectedTextItem?.rect?.X,
                        ],
                        [
                          "width",
                          selectedTextEdit?.rect?.W ??
                            selectedTextItem?.rect?.W,
                        ],
                        [
                          "y",
                          selectedTextEdit?.rect?.Y ??
                            selectedTextItem?.rect?.Y,
                        ],
                        [
                          "height",
                          selectedTextEdit?.rect?.H ??
                            selectedTextItem?.rect?.H,
                        ],
                      ].map(([key, value]) => (
                        <TextField
                          key={key}
                          label={key}
                          disabled={loading}
                          size="small"
                          type="number"
                          value={formatNumber(value, 5)}
                          onChange={(event) =>
                            onFieldChange?.(key, Number(event.target.value))
                          }
                          inputProps={{ step: 0.0001 }}
                        />
                      ))}
                    </Box>
                  </AccordionDetails>
                </Accordion>

                <Accordion
                  expanded={expandedSections.general}
                  onChange={handleSectionToggle("general")}
                  disableGutters
                  sx={sectionSx}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography sx={{ fontWeight: 600 }}>PDF Text</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 1.25,
                      }}
                    >
                      <TextField
                        label="Original Text"
                        size="small"
                        value={selectedTextItem?.text || ""}
                        InputProps={{ readOnly: true }}
                      />
                      <TextField
                        label="Replacement Text"
                        size="small"
                        disabled={loading}
                        multiline
                        minRows={2}
                        value={
                          selectedTextEdit?.new_text ??
                          selectedTextItem?.text ??
                          ""
                        }
                        onChange={(event) =>
                          onFieldChange?.("text", event.target.value)
                        }
                      />
                      <TextField
                        label="Action"
                        size="small"
                        disabled={loading}
                        select
                        value={selectedTextEdit?.action || "replace"}
                        onChange={(event) =>
                          onFieldChange?.("action", event.target.value)
                        }
                      >
                        <MenuItem value="replace">replace</MenuItem>
                        <MenuItem value="move">move</MenuItem>
                        <MenuItem value="delete">delete</MenuItem>
                      </TextField>
                    </Box>
                  </AccordionDetails>
                </Accordion>
              </>
            ) : selectedField ? (
              <>
                {!selectedField?.hidden && (
                  <Accordion
                    expanded={expandedSections.geometry}
                    onChange={handleSectionToggle("geometry")}
                    disableGutters
                    sx={sectionSx}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography sx={{ fontWeight: 600 }}>Geometry</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "1fr 1fr",
                          gap: 1.25,
                        }}
                      >
                        {geometryFieldConfigs.map((fieldConfig) => (
                          <TextField
                            key={fieldConfig.key}
                            label={fieldConfig.label}
                            size="small"
                            type="number"
                            disabled={loading}
                            value={geometryDrafts[fieldConfig.key] ?? ""}
                            onChange={(event) => {
                              setGeometryDrafts((prev) => ({
                                ...prev,
                                [fieldConfig.key]: event.target.value,
                              }));
                            }}
                            onBlur={() =>
                              commitGeometryValue(
                                fieldConfig.key,
                                fieldConfig.aliases,
                                fieldConfig.decimals,
                              )
                            }
                            inputProps={{ step: 0.0001 }}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  pt
                                </InputAdornment>
                              ),
                            }}
                          />
                        ))}
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                )}

                <Accordion
                  expanded={expandedSections.general}
                  onChange={handleSectionToggle("general")}
                  disableGutters
                  sx={sectionSx}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography sx={{ fontWeight: 600 }}>General</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 1.25,
                      }}
                    >
                      <TextField
                        label="Label"
                        size="small"
                        disabled={loading}
                        value={getValue(
                          selectedField,
                          "field_label",
                          "label",
                          "placeholder",
                          "name",
                        )}
                        onChange={(event) => {
                          const nextValue = event.target.value;
                          onFieldChange?.("field_label", nextValue);
                          onFieldChange?.("label", nextValue);
                        }}
                      />
                    </Box>
                  </AccordionDetails>
                </Accordion>

                {!selectedField?.hidden && (
                  <Accordion
                    expanded={expandedSections.appearance}
                    onChange={handleSectionToggle("appearance")}
                    disableGutters
                    sx={sectionSx}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography sx={{ fontWeight: 600 }}>
                        Appearance
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 1.25,
                        }}
                      >
                        <TextField
                          label="Line Width"
                          size="small"
                          type="number"
                          disabled={
                            (!selectedField?.outline &&
                              !["PDFRectangle", "PDFLine"].includes(
                                selectedField?.type,
                              )) ||
                            loading
                          }
                          value={
                            getValue(selectedField, "lineWidth", "alignment") ||
                            1
                          }
                          onChange={(event) =>
                            onFieldChange?.(
                              "lineWidth",
                              Number(event.target.value),
                            )
                          }
                        />

                        <TextField
                          label="Alignment"
                          size="small"
                          disabled={loading}
                          select
                          value={
                            getValue(selectedField, "textAlign", "alignment") ||
                            "left"
                          }
                          onChange={handleChange("textAlign")}
                        >
                          {alignmentOptions.map((option) => (
                            <MenuItem key={option} value={option}>
                              {option}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                )}

                <Accordion
                  expanded={expandedSections.advance}
                  onChange={handleSectionToggle("advance")}
                  disableGutters
                  sx={sectionSx}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography sx={{ fontWeight: 600 }}>Advance</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 1.25,
                      }}
                    >
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{
                            display: "block",
                            mb: 0.5,
                            fontSize: "14px !important",
                            color: "#666",
                          }}
                        >
                          Parents
                        </Typography>

                        {/* Chips */}
                        {parents.length > 0 && (
                          <Box
                            sx={{
                              display: "flex",
                              flexWrap: "wrap",
                              gap: 0.5,
                              mb: 0.75,
                            }}
                          >
                            {parents.map((parent) => {
                              const field = selectFields.find(
                                (f) =>
                                  f.field_instance_id ===
                                  parent.field_instance_id,
                              );
                              return (
                                <Chip
                                  key={parent.field_instance_id}
                                  label={
                                    parent?.field_label ??
                                    field?.name ??
                                    parent.name
                                  }
                                  size="small"
                                  onDelete={() =>
                                    onFieldChange?.(
                                      "parents",
                                      parents.filter(
                                        (p) =>
                                          p.field_instance_id !==
                                          parent.field_instance_id,
                                      ),
                                    )
                                  }
                                />
                              );
                            })}
                          </Box>
                        )}

                        {/* Add parent button */}
                        <Button
                          size="medium"
                          variant="outlined"
                          startIcon={<AddIcon />}
                          disabled={loading}
                          onClick={() => setParentsDialogOpen(true)}
                          sx={{
                            fontSize: 11,
                          }}
                        >
                          Add parent
                        </Button>
                      </Box>

                      {selectedCombineField?.field_type === "total" && (
                        <>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={getValue(selectedField, "isEditable")}
                                onChange={(e) =>
                                  onFieldChange?.(
                                    "isEditable",
                                    e.target.checked,
                                  )
                                }
                              />
                            }
                            label="Is Editable?"
                          />

                          <Box>
                            <Typography
                              variant="caption"
                              sx={{
                                display: "block",
                                mb: 0.5,
                                fontSize: "14px !important",
                                color: "#666",
                              }}
                            >
                              Setup Computed Value
                            </Typography>

                            {getValue(selectedField, "formulaFields")?.length >
                              0 && (
                              <Box
                                sx={{
                                  bgcolor: "action.hover",
                                  borderRadius: 1,
                                  p: 1,
                                  display: "flex",
                                  flexWrap: "wrap",
                                  gap: 0.75,
                                  alignItems: "center",
                                  mb: 1,
                                }}
                              >
                                {getValue(selectedField, "formulaFields").map(
                                  (e, i) => (
                                    <Box
                                      key={i}
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 0.5,
                                      }}
                                    >
                                      {i > 0 && e?.operator?.value && (
                                        <Typography sx={{ fontSize: "14px" }}>
                                          {OP_SYMBOL[e.operator.value]}
                                        </Typography>
                                      )}
                                      {e?.field?.name ? (
                                        <Typography sx={{ fontSize: "14px" }}>
                                          {e.field.name}
                                        </Typography>
                                      ) : (
                                        <Typography sx={{ fontSize: "14px" }}>
                                          {e.value}
                                        </Typography>
                                      )}
                                    </Box>
                                  ),
                                )}
                              </Box>
                            )}
                            {/* Add parent button */}
                            <Button
                              size="medium"
                              variant="outlined"
                              startIcon={<AddIcon />}
                              disabled={loading}
                              onClick={() => setConfigureFormulaOpen(true)}
                              sx={{
                                fontSize: 11,
                              }}
                            >
                              Setup Computed Value
                            </Button>
                          </Box>
                        </>
                      )}

                      {groupedFields && groupedFields?.length > 0 && (
                        <Box>
                          <Box
                            display={"flex"}
                            alignItems={"center"}
                            justifyContent={"space-between"}
                            gap={1}
                            mb={1}
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                display: "block",
                                mb: 0.5,
                                fontSize: "14px !important",
                                color: "#666",
                              }}
                            >
                              Field Group Visibility
                            </Typography>
                            <IconButton
                              size="small"
                              disabled={loading}
                              onClick={() =>
                                handleRemoveFieldFromRow(
                                  selectedField?.row,
                                  true,
                                )
                              }
                              sx={{
                                border: "1px solid",
                                borderColor: "error.light",
                                borderRadius: 1.5,
                                backgroundColor: "transparent",
                                p: "4px !important",
                              }}
                            >
                              <DeleteIcon
                                sx={{ fontSize: "1rem" }}
                                color="error"
                              />
                            </IconButton>
                          </Box>
                          <Box
                            display={"flex"}
                            alignItems="center"
                            gap={1}
                            flexDirection={"column"}
                          >
                            {groupedFields.map((f, i) => {
                              const displayLabel =
                                textOverlayDisplayMode === "none"
                                  ? `${f?.label} (${f?.name})`
                                  : textOverlayDisplayMode === "label"
                                    ? f?.label
                                    : f?.name;

                              return (
                                <Box
                                  display={"flex"}
                                  alignItems="center"
                                  gap={1}
                                  key={f?.name}
                                  justifyContent={"space-between"}
                                  width={"100%"}
                                >
                                  <Typography sx={{ fontSize: "14px" }}>
                                    {displayLabel}
                                  </Typography>
                                  <DeleteIcon
                                    sx={{ fontSize: "1rem", cursor: "pointer" }}
                                    color="error"
                                    onClick={() =>
                                      handleRemoveFieldFromRow(
                                        f?.row,
                                        false,
                                        displayLabel,
                                        f?.options,
                                      )
                                    }
                                  />
                                </Box>
                              );
                            })}
                          </Box>
                        </Box>
                      )}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              </>
            ) : (
              <Box sx={{ pt: 2 }}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  align="center"
                >
                  Select a field on the canvas to inspect its details.
                </Typography>
              </Box>
            )}
          </Box>
          <FormulaDialog
            open={configureFormulaOpen}
            onClose={() => setConfigureFormulaOpen(false)}
            fieldOptions={allFields
              ?.filter(
                (f) =>
                  ["number", "dollar", "total"].includes(f?.field_type) &&
                  f?.name !== selectedField?.name,
              )
              ?.map((f) => ({
                name: f.name,
                field_label: f.field_label,
              }))}
            formulaFields={getValue(selectedField, "formulaFields")}
            onFieldChange={onFieldChange}
          />
          <ParentFieldDialog
            open={parentsDialogOpen}
            allFields={selectFields?.filter((f) =>
              f.field_instance_id
                ? f.field_instance_id !== selectedField?.field_instance_id
                : !f?.options?.some(
                    (opt) =>
                      opt?.field_instance_id ===
                      selectedField?.field_instance_id,
                  ),
            )}
            selectedKeys={parents}
            onClose={() => setParentsDialogOpen(false)}
            onConfirm={(nextParents) => {
              onFieldChange?.("parents", nextParents);
              setParentsDialogOpen(false);
            }}
          />
        </Box>
      </Box>
    </Fade>
  );
};

export default ObjectInspectorDrawer;
